var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import merge from "lodash.merge";
import { generateTrackIdentifier, getActionFromTrackIdentifier, ModelEventAction, ModelSubscribeEventBatchSize, ModelSubscribeEventKeepAliveCheckPendingPeriod, ModelSubscribeEventKeepAlivePeriod, ModelSubscribeEventQueWaitTime, ModelSubscriberEventFirstContentSendSize, UpdateStrategy, ManualUpdateTypes, } from "./types";
export var ModelRequestStrategy;
(function (ModelRequestStrategy) {
    ModelRequestStrategy[ModelRequestStrategy["parallel"] = 0] = "parallel";
    ModelRequestStrategy[ModelRequestStrategy["sequence"] = 1] = "sequence";
})(ModelRequestStrategy || (ModelRequestStrategy = {}));
export class ModelEventSubscriber {
    static simpleSubscribe(params, onEvent) {
        params.track(params.trackName, (header, body) => __awaiter(this, void 0, void 0, function* () {
            if (header === params.trackName) {
                onEvent(body);
            }
        }));
        return {
            unsubscribe: () => params.removeTrack(params.trackName),
        };
    }
    constructor(config) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        this.queue = [];
        this.sendQueue = [];
        this.queueTimeout = null;
        this.keepAliveTimeout = null;
        this.modelState = new Map();
        this.modelStateIndexes = [];
        this.trackIdentifiers = [];
        this.generateTrackIdentifier = (action) => generateTrackIdentifier(this.config.trackModelName, action);
        this.config = Object.assign(Object.assign({}, config), { getAll: (_a = config.getAll) !== null && _a !== void 0 ? _a : (() => this.getBatchDefault.bind(this)([], this.config.modelRequest.strategy)), modelRequest: {
                strategy: (_c = (_b = config.modelRequest) === null || _b === void 0 ? void 0 : _b.strategy) !== null && _c !== void 0 ? _c : ModelRequestStrategy.parallel,
            }, batchSize: config.batchSize || ModelSubscribeEventBatchSize.auto, firstContentSend: typeof config.firstContentSend !== "object"
                ? false
                : {
                    size: ((_d = config.firstContentSend) === null || _d === void 0 ? void 0 : _d.size) ||
                        ModelSubscriberEventFirstContentSendSize.auto,
                }, queWaitTime: config.queWaitTime || ModelSubscribeEventQueWaitTime.default, metaFields: config.metaFields || {}, customTriggers: config.customTriggers || {}, keepAlive: {
                period: config.keepAlive.period ||
                    ModelSubscribeEventKeepAlivePeriod.default,
                pendingPeriod: config.keepAlive.pendingPeriod ||
                    ModelSubscribeEventKeepAliveCheckPendingPeriod.default,
                onKeepAlive: config.keepAlive.onKeepAlive,
            }, optimization: {
                publisherModelEventOptimization: (_f = (_e = config.optimization) === null || _e === void 0 ? void 0 : _e.publisherModelEventOptimization) !== null && _f !== void 0 ? _f : true,
                subscriberPostModelEventOptimization: (_h = (_g = config.optimization) === null || _g === void 0 ? void 0 : _g.subscriberPostModelEventOptimization) !== null && _h !== void 0 ? _h : true,
            } });
        this.init();
    }
    getBatchDefault(idList, strategy) {
        return __awaiter(this, void 0, void 0, function* () {
            let ids = idList;
            if (!idList || idList.length === 0) {
                ids = yield this.config.getAllIds();
            }
            const usedStrategy = strategy || this.config.modelRequest.strategy;
            if (usedStrategy === ModelRequestStrategy.parallel) {
                return Promise.all(ids.map((id) => this.config.getById(id)));
            }
            const result = [];
            for (let i = 0; i < ids.length; i++) {
                result.push(yield this.config.getById(ids[i]));
            }
            return result;
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            this.keepAliveTimeout = setTimeout(this.onKeepAliveCheck.bind(this), this.config.keepAlive.period);
            this.trackIdentifiers = [
                this.generateTrackIdentifier(ModelEventAction.UPDATE),
                this.generateTrackIdentifier(ModelEventAction.DELETE),
                ...Object.keys(this.config.customTriggers).map((key) => this.generateTrackIdentifier(key)),
            ];
            this.trackIdentifiers.forEach((trackIdentifier) => this.config.track(trackIdentifier, this.onTrackEvent.bind(this)));
            this.pushToSendQueue({
                modelName: this.config.trackModelName,
                idParamName: this.config.idParamName,
                action: ModelEventAction.MANUAL_UPDATE,
                data: {
                    id: ManualUpdateTypes.INIT,
                },
            }, ...Object.entries((yield Promise.all(Object.entries(this.config.metaFields).map((_a) => __awaiter(this, [_a], void 0, function* ([key, item]) {
                return [
                    key,
                    yield item.onModelChange(),
                ];
            })))).reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {})).map(([key, value]) => {
                const event = {
                    modelName: this.config.trackModelName,
                    idParamName: this.config.idParamName,
                    action: ModelEventAction.META,
                    data: {
                        id: key,
                        data: value,
                    },
                };
                return event;
            }));
            const prepareModelCreateEvent = ([id, data, index]) => {
                const createEvent = {
                    modelName: this.config.trackModelName,
                    idParamName: this.config.idParamName,
                    action: ModelEventAction.UPDATE,
                    data: {
                        id,
                        data,
                        index,
                        updateStrategy: UpdateStrategy.REPLACE,
                    },
                };
                return createEvent;
            };
            if (this.config.firstContentSend !== false) {
                const allIds = yield this.config.getAllIds();
                const idsWithIndexes = allIds.map((id, index) => [id, index]);
                const firstContentIds = idsWithIndexes.slice(0, this.config.firstContentSend.size === "auto"
                    ? Math.ceil(allIds.length / 10)
                    : this.config.firstContentSend.size);
                const firstPart = yield this.getBatchDefault(firstContentIds.map(([id]) => id));
                firstContentIds.forEach(([id, posIndex], index) => {
                    const item = firstPart[index];
                    this.setModel(id, item, posIndex);
                });
                this.pushToSendQueue(...firstContentIds
                    .map(([id, posIndex], index) => [
                    id,
                    firstPart[index],
                    posIndex,
                ])
                    .map(prepareModelCreateEvent));
                const lastPartIds = idsWithIndexes.slice(firstContentIds.length);
                if (lastPartIds.length > 0) {
                    const lastPart = yield this.getBatchDefault(lastPartIds.map(([id]) => id));
                    lastPartIds.forEach(([id, posIndex], index) => {
                        const item = lastPart[index];
                        this.setModel(id, item, posIndex);
                    });
                    this.pushToSendQueue(...lastPartIds
                        .map(([id, posIndex], index) => [
                        id,
                        lastPart[index],
                        posIndex,
                    ])
                        .map(prepareModelCreateEvent));
                }
            }
            else {
                const allObjects = yield this.config.getAll();
                const allObjectData = allObjects.map((item, index) => [
                    item[this.config.idParamName],
                    item,
                    index,
                ]);
                allObjectData.forEach((item) => {
                    this.setModel(...item);
                });
                this.pushToSendQueue(...allObjectData.map(prepareModelCreateEvent));
            }
        });
    }
    setModel(id, data, index = null) {
        this.modelState.set(id, data);
        if (index !== null)
            this.setIdToIndex(id, index);
    }
    deleteModel(id) {
        this.modelState.delete(id);
        this.modelStateIndexes.splice(this.modelStateIndexes.indexOf(id), 1);
    }
    setIdToIndex(id, index = -1) {
        const isModelPresent = this.modelState.has(id);
        if (!isModelPresent)
            throw new Error(`Model with id ${id} is not present in modelState`);
        const currentIndex = this.modelStateIndexes.indexOf(id);
        if (currentIndex !== -1) {
            this.modelStateIndexes.splice(currentIndex, 1);
        }
        if (index !== -1) {
            this.modelStateIndexes.push(id);
        }
        else {
            this.modelStateIndexes.splice(index, 0, id);
        }
    }
    getStateIdList() {
        return this.modelStateIndexes;
    }
    pushToQueue(...events) {
        if (events.length) {
            const currentQueLength = this.queue.length;
            const currentSendQueLength = this.sendQueue.length;
            this.queue.push(...events);
            if (currentQueLength === 0 && currentSendQueLength === 0) {
                setTimeout(this.onQueueStep.bind(this), 0);
            }
        }
    }
    pushToSendQueue(...events) {
        if (events.length) {
            const currentQueLength = this.queue.length;
            const currentSendQueLength = this.sendQueue.length;
            this.sendQueue.push(...events);
            if (currentQueLength === 0 && currentSendQueLength === 0) {
                setTimeout(this.onQueueStep.bind(this), 0);
            }
        }
    }
    onKeepAliveCheck() {
        return __awaiter(this, void 0, void 0, function* () {
            const isAlive = yield Promise.race([
                this.config.keepAlive.onKeepAlive(),
                new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(false);
                    }, this.config.keepAlive.pendingPeriod);
                }),
            ]);
            if (!isAlive) {
                yield this.unsubscribe();
            }
            else {
                this.keepAliveTimeout = setTimeout(this.onKeepAliveCheck.bind(this), this.config.keepAlive.period);
            }
        });
    }
    optimizeQueue() {
        return __awaiter(this, void 0, void 0, function* () {
            this.queue.reduce((acc, item, index) => {
                const itemIndex = acc.findIndex((i) => i.id === item.body.id);
                if (itemIndex === -1) {
                    return [
                        ...acc,
                        {
                            id: item.body.id,
                            actions: [
                                {
                                    name: getActionFromTrackIdentifier(item.header),
                                    index,
                                },
                            ],
                        },
                    ];
                }
                acc[itemIndex].actions.push({
                    name: getActionFromTrackIdentifier(item.header),
                    index,
                });
                return acc;
            }, [])
                .filter((item) => item.actions.length > 1)
                .forEach((item) => {
                var _a;
                if (Array.from(new Set(item.actions.map((action) => action.name))).length === 1 &&
                    (((_a = this.config.customTriggers[item.actions[0].name]) === null || _a === void 0 ? void 0 : _a.allowOptimization) ||
                        !this.config.customTriggers[item.actions[0].name])) {
                    item.actions.slice(0, -1).forEach((action) => {
                        this.queue.splice(action.index, 1);
                    });
                    const updateItem = item.actions.slice(-1)[0];
                    if (this.queue[updateItem.index]
                        .body.updateStrategy === UpdateStrategy.MERGE) {
                        this.queue[updateItem.index]
                            .body.data = undefined;
                        this.queue[updateItem.index]
                            .body.updateStrategy = UpdateStrategy.REPLACE;
                    }
                }
                else if (item.actions.some((action) => action.name === ModelEventAction.DELETE) &&
                    !item.actions.some((action) => {
                        var _a;
                        return (_a = this.config.customTriggers[action.name]) === null || _a === void 0 ? void 0 : _a.allowOptimization;
                    })) {
                    const reverseActions = [...item.actions].reverse();
                    const currentDeleteIndex = reverseActions.findIndex((action) => action.name === ModelEventAction.DELETE);
                    const currentUpdateIndex = reverseActions.findIndex((action) => action.name !== ModelEventAction.DELETE);
                    if (currentUpdateIndex < currentDeleteIndex) {
                        // since delete is last, we can just remove all besides delete
                        reverseActions.splice(currentDeleteIndex, 1);
                        reverseActions.forEach((action) => {
                            this.queue.splice(action.index, 1);
                        });
                    }
                    else {
                        // since update is last, we can just remove all besides update
                        const updateItem = reverseActions.splice(currentUpdateIndex, 1);
                        if (this.queue[updateItem[0].index]
                            .body.updateStrategy === UpdateStrategy.MERGE) {
                            //     could cause issues if previous update was merge also
                            //     remove data and change updateStrategy to replace for right now
                            this.queue[updateItem[0].index]
                                .body.data = undefined;
                            this.queue[updateItem[0].index]
                                .body.updateStrategy = UpdateStrategy.REPLACE;
                        }
                        reverseActions.forEach((action) => {
                            this.queue.splice(action.index, 1);
                        });
                    }
                }
            });
        });
    }
    optimizeSendQueue() {
        return __awaiter(this, void 0, void 0, function* () {
            const metaEvents = Object.values(this.sendQueue
                .filter((event) => event.action === ModelEventAction.META)
                .reduce((acc, event) => (Object.assign(Object.assign({}, acc), { [event.data.id]: event })), {})).reduce((acc, value) => [...acc, value], []);
            const systemEvents = this.sendQueue.filter((event) => event.action !== ModelEventAction.MANUAL_UPDATE);
            const modelEvents = this.sendQueue.filter((event) => [ModelEventAction.MANUAL_UPDATE, ModelEventAction.META].indexOf(event.action) === -1);
            const uniqModelEvents = Array.from(new Set(modelEvents.map((event) => event.data.id)))
                .map((id) => {
                const events = modelEvents
                    .filter((event) => event.data.id === id)
                    .map((event, index) => ({
                    event,
                    index,
                }));
                const deleteEvent = events
                    .slice()
                    .reverse()
                    .find(({ event }) => event.action === ModelEventAction.DELETE);
                const updateEvents = events.filter(({ event }) => [
                    ModelEventAction.DELETE,
                    ModelEventAction.META,
                    ModelEventAction.MANUAL_UPDATE,
                ].indexOf(event.action) === -1);
                const excludedEvents = [];
                if (deleteEvent) {
                    if (deleteEvent.index >
                        Math.max(...updateEvents.map(({ index }) => index))) {
                        return [deleteEvent.event];
                    }
                    excludedEvents.push(...events
                        .map(({ index }) => index)
                        .filter((index) => deleteEvent.index >= index));
                }
                const clearedUpdateEvents = updateEvents
                    .filter(({ index }) => !excludedEvents.includes(index))
                    .map((item, index) => (Object.assign({ internalIndex: index }, item)));
                if (clearedUpdateEvents.length > 1) {
                    const reversedEvents = clearedUpdateEvents
                        .slice()
                        .reverse();
                    const lastUpdateEvent = reversedEvents.find(({ event }) => event.action === ModelEventAction.UPDATE);
                    const lastUpdateReturn = () => [
                        lastUpdateEvent.event,
                        ...clearedUpdateEvents
                            .filter(({ event }) => [
                            ModelEventAction.UPDATE_INDEX,
                            ModelEventAction.UPDATE,
                        ].indexOf(event.action) === -1)
                            .map(({ event }) => event),
                    ];
                    if (lastUpdateEvent.internalIndex ===
                        clearedUpdateEvents.length - 1) {
                        if (lastUpdateEvent.event
                            .data.updateStrategy === UpdateStrategy.REPLACE) {
                            return lastUpdateReturn();
                        }
                    }
                    else {
                        const lastUpdateIndexEvent = reversedEvents.find(({ event }) => event.action === ModelEventAction.UPDATE_INDEX);
                        if (lastUpdateIndexEvent.internalIndex >
                            lastUpdateEvent.internalIndex) {
                            lastUpdateEvent.event.data.index = lastUpdateIndexEvent.event.data.index;
                        }
                        if (lastUpdateEvent.event
                            .data.updateStrategy === UpdateStrategy.REPLACE) {
                            return lastUpdateReturn();
                        }
                    }
                    excludedEvents.push(...clearedUpdateEvents
                        .filter(({ event }) => event.action ===
                        ModelEventAction.UPDATE_INDEX)
                        .map(({ index }) => index));
                }
                return events
                    .filter(({ index }) => !excludedEvents.includes(index))
                    .map(({ event }) => event);
            })
                .flat();
            this.sendQueue = [...metaEvents, ...systemEvents, ...uniqModelEvents];
        });
    }
    updateMetaFields(triggers_1) {
        return __awaiter(this, arguments, void 0, function* (triggers, doPush = true) {
            const doTriggerFilter = (items) => triggers
                ? items.filter(([, item]) => item.modelTriggers.some((trigger) => triggers.includes(trigger)) ||
                    (item.customTriggers || []).some((customTrigger) => triggers.includes(customTrigger)))
                : items;
            const items = (yield Promise.all(doTriggerFilter(Object.entries(this.config.metaFields)).map((_a) => __awaiter(this, [_a], void 0, function* ([key, item]) { return [key, yield item.onModelChange()]; })))).map(([key, value]) => ({
                modelName: this.config.trackModelName,
                idParamName: this.config.idParamName,
                action: ModelEventAction.META,
                data: {
                    id: key,
                    data: value,
                },
            }));
            return doPush ? this.pushToSendQueue(...items) : items;
        });
    }
    onQueueStep() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.queue.length) {
                let shouldUpdateIndexes = false;
                let newIdList = null;
                if (this.config.optimization.publisherModelEventOptimization)
                    yield this.optimizeQueue();
                const triggers = this.queue.map((event) => getActionFromTrackIdentifier(event.header));
                for (let i = 0; i < this.queue.length; i++) {
                    const eventResponse = yield this.performQue(this.queue[i], newIdList);
                    if (eventResponse.shouldUpdateIndexes) {
                        shouldUpdateIndexes = true;
                    }
                    if (eventResponse.newIdList) {
                        newIdList = eventResponse.newIdList;
                    }
                }
                this.queue = [];
                if (shouldUpdateIndexes) {
                    yield this.findIndexDiff(newIdList);
                }
                yield this.updateMetaFields(triggers);
            }
            if (this.sendQueue.length) {
                if (this.config.optimization.subscriberPostModelEventOptimization)
                    yield this.optimizeSendQueue();
                if (this.config.batchSize === ModelSubscribeEventBatchSize.auto) {
                    this.config.onModelEvent(this.sendQueue);
                    this.sendQueue = [];
                }
                else {
                    const batch = this.sendQueue.splice(0, this.config.batchSize);
                    this.config.onModelEvent(batch);
                }
            }
            if (this.queue.length || this.sendQueue.length) {
                this.queueTimeout = setTimeout(this.onQueueStep.bind(this), this.config.queWaitTime);
            }
        });
    }
    performQue(event, newIdList) {
        return __awaiter(this, void 0, void 0, function* () {
            const action = getActionFromTrackIdentifier(event.header);
            const id = event.body[this.config.idParamName];
            switch (action) {
                case ModelEventAction.UPDATE:
                    return this.onUpdate(id, event.body, newIdList);
                case ModelEventAction.DELETE:
                    return { shouldUpdateIndexes: true };
                default:
                    return this.config.customTriggers[event.header].on({
                        getAll: this.config.getAll,
                        getAllIds: this.config.getAllIds,
                        onUpdate: this.onUpdate,
                        modelState: this.modelState,
                    }, event.body);
            }
        });
    }
    onTrackEvent(header, body) {
        return __awaiter(this, void 0, void 0, function* () {
            this.pushToQueue({ header, body });
        });
    }
    findIndexDiff(existedNewIdList_1) {
        return __awaiter(this, arguments, void 0, function* (existedNewIdList, forceMetaUpgrade = false) {
            const currentIdList = this.getStateIdList();
            const newIdList = existedNewIdList || (yield this.config.getAllIds());
            const eventsSet = new Set();
            const lookForData = (id) => __awaiter(this, void 0, void 0, function* () {
                const model = this.modelState.get(id);
                return model || this.config.getById(id);
            });
            const updateIndexConfig = (id, index) => ({
                modelName: this.config.trackModelName,
                idParamName: this.config.idParamName,
                action: ModelEventAction.UPDATE_INDEX,
                data: {
                    id,
                    index,
                },
            });
            const que = [
                ...currentIdList
                    .filter((id) => !newIdList.includes(id))
                    .map((id) => {
                    eventsSet.add(ModelEventAction.DELETE);
                    const deleteEvent = {
                        modelName: this.config.trackModelName,
                        idParamName: this.config.idParamName,
                        action: ModelEventAction.DELETE,
                        data: { id },
                    };
                    return deleteEvent;
                }),
                ...(yield Promise.all(newIdList.map((id, index) => __awaiter(this, void 0, void 0, function* () {
                    if (id !== currentIdList[index]) {
                        if (currentIdList.indexOf(id) !== -1) {
                            // already exists, do index update only
                            eventsSet.add(ModelEventAction.UPDATE_INDEX);
                            return updateIndexConfig(id, index);
                        }
                        const data = yield lookForData(id);
                        this.setModel(id, data, index);
                        eventsSet.add(ModelEventAction.UPDATE);
                        return {
                            modelName: this.config.trackModelName,
                            idParamName: this.config.idParamName,
                            action: ModelEventAction.UPDATE,
                            data: {
                                id,
                                data,
                                index,
                                updateStrategy: UpdateStrategy.REPLACE,
                            },
                        };
                    }
                    eventsSet.add(ModelEventAction.UPDATE_INDEX);
                    return updateIndexConfig(id, index);
                })))),
                ...(yield this.updateMetaFields(forceMetaUpgrade ? false : [...eventsSet], false)),
            ];
            // remove deleted models from state
            currentIdList
                .filter((id) => !newIdList.includes(id))
                .forEach((id) => {
                this.deleteModel(id);
            });
            if (que.length) {
                this.pushToSendQueue(...que);
            }
        });
    }
    onUpdate(id, body, existedNewIdList) {
        return __awaiter(this, void 0, void 0, function* () {
            const newIdList = existedNewIdList || (yield this.config.getAllIds());
            const modelHasLocal = this.modelState.has(id);
            const indexInNew = newIdList.indexOf(id);
            const updateStrategy = body.updateStrategy || "replace";
            const dataFromBody = !!body.data;
            const data = body.data;
            if (indexInNew !== -1) {
                if (updateStrategy === UpdateStrategy.REPLACE) {
                    this.setModel(id, dataFromBody
                        ? yield this.config.sanitizeModel(data)
                        : yield this.config.getById(id), indexInNew);
                }
                else if (updateStrategy === UpdateStrategy.MERGE &&
                    body.data &&
                    modelHasLocal) {
                    this.setModel(id, yield this.config.sanitizeModel(merge(this.modelState.get(id), data)), indexInNew);
                }
                this.pushToSendQueue({
                    modelName: this.config.trackModelName,
                    idParamName: this.config.idParamName,
                    action: ModelEventAction.UPDATE,
                    data: {
                        id,
                        data: this.modelState.get(id),
                        index: indexInNew,
                        updateStrategy: UpdateStrategy.REPLACE,
                    },
                });
                return { shouldUpdateIndexes: true, newIdList, __stage: 1 };
            }
            if (modelHasLocal) {
                return { shouldUpdateIndexes: true, newIdList, __stage: 2 };
            }
            return { shouldUpdateIndexes: false, newIdList, __stage: 3 };
        });
    }
    regenerateState() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.unsubscribe();
            const currentIdList = this.getStateIdList();
            this.modelState.clear();
            this.modelStateIndexes = [];
            this.pushToSendQueue(...[
                {
                    modelName: this.config.trackModelName,
                    idParamName: this.config.idParamName,
                    action: ModelEventAction.MANUAL_UPDATE,
                    data: {
                        id: ManualUpdateTypes.REGENERATE_STATE,
                    },
                },
                ...currentIdList.map((id) => {
                    const deleteEvent = {
                        modelName: this.config.trackModelName,
                        idParamName: this.config.idParamName,
                        action: ModelEventAction.DELETE,
                        data: { id },
                    };
                    return deleteEvent;
                }),
            ]);
            yield this.init();
        });
    }
    regenerateIndexes() {
        return __awaiter(this, arguments, void 0, function* (forceMetaUpgrade = false) {
            this.pushToSendQueue({
                modelName: this.config.trackModelName,
                idParamName: this.config.idParamName,
                action: ModelEventAction.MANUAL_UPDATE,
                data: {
                    id: ManualUpdateTypes.REGENERATE_INDEXES,
                },
            });
            yield this.findIndexDiff(null, forceMetaUpgrade);
        });
    }
    regenerateMeta() {
        return __awaiter(this, void 0, void 0, function* () {
            this.pushToSendQueue({
                modelName: this.config.trackModelName,
                idParamName: this.config.idParamName,
                action: ModelEventAction.MANUAL_UPDATE,
                data: {
                    id: ManualUpdateTypes.REGENERATE_METADATA,
                },
            });
            yield this.updateMetaFields(false);
        });
    }
    unsubscribe() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.keepAliveTimeout)
                clearTimeout(this.keepAliveTimeout);
            yield Promise.all(this.trackIdentifiers.map((trackIdentifier) => this.config.removeTrack(trackIdentifier)));
        });
    }
}
//# sourceMappingURL=subscriber.js.map